/**
 * Pictures in a text (ADR 0310).
 *
 * Jonathon, 2026-10-24: "we need to be able to send and receive photos via
 * text. it needs to be fully functional." Receiving has worked since ADR
 * 0059; this is the sending half's rules, kept here so the Texts card and
 * `twilio-send` cannot disagree about what may go.
 *
 * What a carrier will carry is the constraint. Twilio takes ten attachments
 * and five megabytes a message, but US carriers quietly drop an MMS much
 * over a megabyte, so a photograph off a phone — five to twelve megabytes,
 * often HEIC — is shrunk to a JPEG no wider than `TEXT_PHOTO_MAX_EDGE`
 * before it goes. That is a picture a customer can read on a phone screen,
 * and it arrives.
 */

/** The most pictures one text carries. Five, not Twilio's ten: five
 * photographs of a roof is a lot of text message, and each one is paid for. */
export const MAX_TEXT_PHOTOS = 5;

/** The longest edge a sent picture is shrunk to, in pixels. */
export const TEXT_PHOTO_MAX_EDGE = 1600;

/** JPEG quality the shrunk picture is written at. */
export const TEXT_PHOTO_QUALITY = 0.85;

/** The biggest a picture may be once shrunk. A 1600px JPEG is far under it;
 * this catches the one that could not be shrunk. */
export const TEXT_PHOTO_MAX_BYTES = 2 * 1024 * 1024;

/** What a phone or a desktop hands over that we will take. HEIC is what an
 * iPhone shoots; it is read and rewritten as JPEG before it is sent. */
export const TEXT_PHOTO_TYPES: readonly string[] = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
];

export function isTextPhotoType(type: string | null | undefined): boolean {
  return TEXT_PHOTO_TYPES.includes((type ?? "").toLowerCase());
}

/**
 * Why this file cannot be added, in a sentence, or null when it can.
 *
 * `count` is how many are already attached. Size is checked after shrinking,
 * by `shrunkPhotoProblem`, because the file as chosen is almost always too
 * big and almost never stays so.
 */
export function photoProblem(
  file: { type: string; name?: string },
  count: number,
): string | null {
  if (count >= MAX_TEXT_PHOTOS)
    return `A text can carry ${MAX_TEXT_PHOTOS} pictures; that is the most.`;
  if (!isTextPhotoType(file.type)) {
    const name = file.name ? `${file.name} is` : "That is";
    return `${name} not a picture a text can carry. JPEG, PNG, GIF, WebP or an iPhone photo.`;
  }
  return null;
}

/** What may actually be uploaded and texted: what a carrier delivers and
 * `twilio-send` attaches. HEIC is taken from the picker only to be rewritten
 * as JPEG; where the browser cannot read it, it is never uploaded. */
export const SENDABLE_PHOTO_TYPES: readonly string[] = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

/**
 * Why the picture, as it stands after shrinking (or failing to), cannot be
 * uploaded at all, or null when it can. The one case is an iPhone photo the
 * browser could not decode, still HEIC (or of no type the browser knows).
 */
export function unsendablePhotoProblem(
  type: string | null | undefined,
): string | null {
  if (SENDABLE_PHOTO_TYPES.includes((type ?? "").toLowerCase())) return null;
  return "This browser cannot read that iPhone photo; send it from the phone or save it as a JPEG first.";
}

/**
 * Why the picture still cannot go for its size, or null. `shrunk` is false
 * when the browser could not decode the file and it is as it was chosen.
 */
export function shrunkPhotoProblem(
  bytes: number,
  shrunk: boolean = true,
): string | null {
  if (bytes <= TEXT_PHOTO_MAX_BYTES) return null;
  if (!shrunk)
    return "That picture is too big to text, and this browser could not shrink it. Send it from the phone or save a smaller copy first.";
  return "That picture is too big to text even after shrinking it.";
}

/**
 * The size a picture is drawn at before it is sent: no edge over
 * `TEXT_PHOTO_MAX_EDGE`, the shape kept. A small picture is left alone.
 */
export function shrunkSize(
  width: number,
  height: number,
  maxEdge: number = TEXT_PHOTO_MAX_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest <= 0) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Where a picture we send is kept: under `outbound/` in the same private
 * bucket the pictures customers send us land in, so one screen reads both.
 * The prefix is what the storage policy lets a signed-in rep write under,
 * and what `twilio-send` will attach: a path outside it is refused.
 */
export const OUTBOUND_MEDIA_PREFIX = "outbound/";

export function outboundMediaPath(id: string, extension: string): string {
  const clean = id.replace(/[^A-Za-z0-9_-]/g, "");
  const ext = extension.replace(/[^a-z0-9]/gi, "").toLowerCase() || "jpg";
  return `${OUTBOUND_MEDIA_PREFIX}${clean}.${ext}`;
}

/** The content type a stored outbound picture is served as, by extension. */
export function outboundMediaType(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  const known: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
  };
  return known[ext] ?? "application/octet-stream";
}
